// src/hooks/AuthContext.js
// Auth state management with proper 2FA enforcement.
// Key fix: loading stays true until BOTH profile AND mfaVerified are resolved.
// This prevents App.js from rendering the dashboard before MFA check completes.

import { createContext, useContext, useState, useEffect, useRef } from "react";
import { supabase } from "../config/supabase";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,        setUser]        = useState(null);
  const [profile,     setProfile]     = useState(null);
  const [loading,     setLoading]     = useState(true);
  // null = unknown (still checking), true = 2FA done or not needed, false = 2FA required
  const [mfaVerified,        setMfaVerified]        = useState(null);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(
    // Persist across re-renders — Supabase clears the URL hash before React re-renders
    () => sessionStorage.getItem("exiq_password_recovery") === "1"
  );

  // Track which userId has been fully resolved (profile + MFA) to avoid duplicate work
  const loadingForRef       = useRef(null);
  const profileDoneRef      = useRef(false);
  const mfaDoneRef          = useRef(false);
  // Ref version of isPasswordRecovery so async callbacks can check it
  const isPasswordRecoveryRef = useRef(
    sessionStorage.getItem("exiq_password_recovery") === "1"
  );

  // If sessionStorage flag is already set on mount (e.g. after page reload),
  // immediately set mfaVerified=true so the spinner doesn't block forever.
  // We use a ref to avoid double-running in StrictMode.
  const recoveryInitRef = useRef(false);
  if (!recoveryInitRef.current && isPasswordRecoveryRef.current) {
    recoveryInitRef.current = true;
    // Schedule for after render to avoid setState during render
    Promise.resolve().then(() => {
      setMfaVerified(true);
      setLoading(false);
    });
  }

  // Only set loading=false when BOTH profile AND mfa status are resolved
  function trySetLoadingDone() {
    if (profileDoneRef.current && mfaDoneRef.current) {
      setLoading(false);
    }
  }

  useEffect(() => {
    // Catch old-style hash flow immediately on mount — before Supabase clears hash
    if (window.location.hash.includes("type=recovery")) {
      sessionStorage.setItem("exiq_password_recovery", "1");
      isPasswordRecoveryRef.current = true;
      setIsPasswordRecovery(true);
      setMfaVerified(true);
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      // PASSWORD_RECOVERY fires when user clicks reset link (PKCE flow).
      // Must be checked BEFORE any other logic — bypasses MFA gate entirely.
      // sessionStorage persists the flag after Supabase clears the URL hash.
      if (_event === "PASSWORD_RECOVERY") {
        sessionStorage.setItem("exiq_password_recovery", "1");
        isPasswordRecoveryRef.current = true;
        setIsPasswordRecovery(true);
        // Cancel any running MFA check
        loadingForRef.current = null;
        profileDoneRef.current = true;
        mfaDoneRef.current = true;
        // Set mfaVerified=true so (user && mfaVerified===null) doesn't spin forever
        setMfaVerified(true);
        setLoading(false);
        return;
      }

      const newUser = session?.user ?? null;
      setUser(newUser);

      if (newUser) {
        // Reset resolution flags for this new session
        if (loadingForRef.current !== newUser.id) {
          loadingForRef.current = newUser.id;
          profileDoneRef.current = false;
          mfaDoneRef.current = false;
          setLoading(true);
          setMfaVerified(null);
          // Load profile and MFA status — loading stays true until both complete
          loadProfile(newUser.id);
          checkMfaStatus(newUser.id);
        }
      } else {
        // Signed out — reset everything
        loadingForRef.current = null;
        profileDoneRef.current = false;
        mfaDoneRef.current = false;
        setProfile(null);
        setMfaVerified(null);
        setLoading(false);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // ── Load profile from DB ──────────────────────────────────────────────────
  async function loadProfile(userId) {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .maybeSingle();
      if (error) console.error("Error loading profile:", error);
      setProfile(data || null);
    } catch (err) {
      console.error("Error loading profile:", err);
    } finally {
      profileDoneRef.current = true;
      trySetLoadingDone();
    }
  }

  // ── Check MFA status — sequential to avoid Supabase lock contention ───────
  async function checkMfaStatus(userId) {
    // Small delay so the auth token lock is released after signIn
    await new Promise(r => setTimeout(r, 150));

    // Bail if password recovery is in progress — never run MFA check during reset
    if (isPasswordRecoveryRef.current) return;

    // Bail if user changed since we started
    if (loadingForRef.current !== userId) return;

    try {
      const { data: assuranceData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (loadingForRef.current !== userId) return;

      const { data: factorsData } = await supabase.auth.mfa.listFactors();
      if (loadingForRef.current !== userId) return;

      const currentLevel    = assuranceData?.currentLevel;
      const verifiedFactors = (factorsData?.totp || []).filter(f => f.status === "verified");
      const hasMfa          = verifiedFactors.length > 0;

      if (hasMfa && currentLevel !== "aal2") {
        // User has 2FA enrolled but hasn't completed the challenge — block dashboard
        setMfaVerified(false);
      } else {
        setMfaVerified(true);
      }
    } catch (err) {
      // Lock errors or network failures — don't block the user
      if (err?.message && !err.message.includes("lock")) {
        console.warn("MFA status check failed:", err.message);
      }
      setMfaVerified(true);
    } finally {
      mfaDoneRef.current = true;
      trySetLoadingDone();
    }
  }

  // ── Auth actions ──────────────────────────────────────────────────────────
  async function signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  }

  async function signUp(email, password, fullName) {
    const { data, error } = await supabase.auth.signUp({
      email, password,
      options: { data: { full_name: fullName } },
    });
    if (error) throw error;
    return data;
  }

  async function signOut() {
    sessionStorage.removeItem("exiq_password_recovery");
    isPasswordRecoveryRef.current = false;
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    setMfaVerified(null);
    setIsPasswordRecovery(false);
    profileDoneRef.current = false;
    mfaDoneRef.current = false;
  }

  function clearPasswordRecovery() {
    sessionStorage.removeItem("exiq_password_recovery");
    isPasswordRecoveryRef.current = false;
    setIsPasswordRecovery(false);
  }

  // ── Complete MFA challenge — called from the App-level challenge screen ────
  async function completeMfaChallenge(factorId, code) {
    const { data: challenge, error: cErr } = await supabase.auth.mfa.challenge({ factorId });
    if (cErr) throw cErr;

    const { error: vErr } = await supabase.auth.mfa.verify({
      factorId,
      challengeId: challenge.id,
      code: code.trim(),
    });
    if (vErr) throw vErr;

    // Session is now AAL2 — mark as verified
    setMfaVerified(true);
  }

  const orgId     = profile?.org_id || null;
  const role      = profile?.role   || "viewer";
  const isAdmin   = role === "admin";
  const isManager = role === "admin" || role === "manager";

  return (
    <AuthContext.Provider value={{
      user, profile, loading, orgId, role, isAdmin, isManager,
      mfaVerified,
      isPasswordRecovery,
      clearPasswordRecovery,
      signIn, signUp, signOut,
      completeMfaChallenge,
      reloadProfile: () => user && loadProfile(user.id),
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth() must be used inside <AuthProvider>");
  return ctx;
}
